import { useState, useEffect, useCallback } from 'react'
import { cn } from '@/utils/cn'

interface MobileBottomBarProps {
  children: React.ReactNode
  className?: string
}

/**
 * Fixed bottom bar for mobile with iOS safe area + keyboard handling.
 * Uses the Visual Viewport API to stay pinned to the bottom of the
 * visible viewport even when the iOS keyboard is open.
 * Only visible on mobile (hidden on md: and up).
 */
export function MobileBottomBar({ children, className }: MobileBottomBarProps) {
  const [bottomOffset, setBottomOffset] = useState(0)

  const updatePosition = useCallback(() => {
    const vv = window.visualViewport
    if (!vv) return

    // The difference between the layout viewport and the visual viewport
    // tells us how much the keyboard (or other UI) is covering.
    const offset = window.innerHeight - vv.height - vv.offsetTop
    setBottomOffset(Math.max(0, offset))
  }, [])

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    vv.addEventListener('resize', updatePosition)
    vv.addEventListener('scroll', updatePosition)
    return () => {
      vv.removeEventListener('resize', updatePosition)
      vv.removeEventListener('scroll', updatePosition)
    }
  }, [updatePosition])

  return (
    <div 
      className={cn(
        "fixed left-0 right-0 p-4 bg-[#0a0a0a]/95 backdrop-blur-sm border-t border-white/[0.06] md:hidden z-30 transition-[bottom] duration-100",
        className
      )}
      style={{ 
        bottom: bottomOffset,
        paddingBottom: bottomOffset > 0 ? 16 : 'max(env(safe-area-inset-bottom, 16px), 16px)',
      }}
    >
      {children}
    </div>
  )
}
